import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Button, Spinner, Tabs, Tab } from 'react-bootstrap';
import DesktopLayout from '../layouts/DesktopLayout';
import EditTaskModal from '../components/EditTaskModal';
import GuestReviewCard from '../components/guestReviews/GuestReviewCard';
import LateCleanRow from '../components/guestReviews/LateCleanRow';
import ReplyCard from '../components/guestReviews/ReplyCard';
import { useQueryClient } from '@tanstack/react-query';
import ComplaintsTab from '../components/complaints/ComplaintsTab';
import GuestVoiceTab from '../components/guestReviews/GuestVoiceTab';
import { TEXT as VOICE_TEXT } from '../config/guestVoiceLabels';
import taskApi from '../services/taskApi';
import { useGuestReviewQueue } from '../hooks/useGuestReviews';
import { useComplaintCounts, COMPLAINT_QUEUE_KEY, COMPLAINT_COUNTS_KEY } from '../hooks/useComplaints';
import { formatCount } from '../utils/numberFormatter';
import { TEXT } from '../config/guestReviewLabels';
import { TEXT as COMPLAINT_TEXT } from '../config/complaintLabels';
import '../styles/GuestReviewsPage.css';

/**
 * «التقييمات والشكاوى» — the ops Reviews page (tasks/post-checkout-review-and-replies.md L27, Phases 2–3), with
 * guest complaints as its third tab (tasks/complaints-to-fixes.md C19 — one page for ops, not two).
 *
 * Four tabs, the open one kept in the URL (`?list=replies` / `?list=complaints` / `?list=voice`):
 *   «تقييم الضيوف المغادرين» — our review of each departed guest (Airbnb) and the thank-you, approved one by one
 *                    (L4, L18); above them, the stays whose turnover clean is still open (L10, L51).
 *   «الرد على التقييمات» — our reply to each guest review, drafted by AI and approved one by one (L20, L23).
 *   «الشكاوى» — guest complaints → fixes: its own lists, open complaint and data (ComplaintsTab), read only
 *                    while the tab is open. Its count is the complaints needing action — the menu counter's half.
 *   «صوت الضيف» — what guests love and what needs improving, per unit (tasks/guest-voice-tab.md step 5;
 *                    GuestVoiceTab), read only while the tab is open; shown only while GUEST_REVIEW_ANALYSIS_ENABLED is
 *                    on (`switches.analysis`). No count in its title: it is a view, not a queue.
 *
 * Arabic-only, like every ops queue page. Counts, days left, what is late, what is missing and what a
 * draft may not say are all decided by the server. Nothing is counted on screen before the server has
 * answered — a zero while loading reads as "nothing to do".
 */
const TABS = ['guests', 'replies', 'complaints', 'voice'];

const GuestReviewsPage = () => {
  const { queue, isLoading, isError, refetch, isFetching } = useGuestReviewQueue();
  const { counts, switches, late, items, replies } = queue;
  const [searchParams, setSearchParams] = useSearchParams();
  // «صوت الضيف» exists only while its switch is on (guest-voice G8) — a link to it otherwise lands on the first tab.
  const tabs = switches.analysis ? TABS : TABS.filter(t => t !== 'voice');
  const activeTab = tabs.includes(searchParams.get('list')) ? searchParams.get('list') : 'guests';
  const ready = !isLoading && !isError;
  // The complaints tab's count — the menu counter's own cached query; no second timer (the menu polls it).
  const { data: complaintCounts } = useComplaintCounts(true, { poll: false });
  const queryClient = useQueryClient();

  const guestsWaiting = counts.needsApproval + counts.needsDecision + counts.late;
  const repliesWaiting = counts.replies.needsApproval + counts.replies.needsDecision + counts.replies.notPublished;
  const tabTitle = (label, waiting) => (ready ? `${label} (${formatCount(waiting)})` : label);
  const complaintsTitle = complaintCounts
    ? `${COMPLAINT_TEXT.tabTitle} (${formatCount(complaintCounts.badge)})`
    : COMPLAINT_TEXT.tabTitle;

  // One refresh for the whole page — the complaints tab reads its own list.
  const refresh = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: COMPLAINT_QUEUE_KEY });
    queryClient.invalidateQueries({ queryKey: COMPLAINT_COUNTS_KEY });
    queryClient.invalidateQueries({ queryKey: ['guest-voice'] });
  };

  // The clean behind a row, opened in place — EditTaskModal needs the whole task, not its id (the
  // BookingGuestRequestsSection pattern). A save refreshes the list: a clean that closes here turns
  // its late row into an item.
  const [editingTask, setEditingTask] = useState(null);
  const [loadingTaskId, setLoadingTaskId] = useState(null);
  const [taskLoadError, setTaskLoadError] = useState(null);

  const openTask = async (taskId) => {
    if (!taskId) return;
    setLoadingTaskId(taskId);
    setTaskLoadError(null);
    try {
      const resp = await taskApi.getTask(taskId);
      const task = resp?.data?.task;
      if (task && task._id) {
        setEditingTask(task);
      } else {
        setTaskLoadError(TEXT.taskLoadError);
      }
    } catch (err) {
      console.error('Failed to load the cleaning task:', err);
      setTaskLoadError(TEXT.taskLoadError);
    } finally {
      setLoadingTaskId(null);
    }
  };

  const handleTaskUpdated = () => {
    setEditingTask(null);
    refetch();
  };

  return (
    <DesktopLayout activeNavItem="guest-reviews" headerTitle={TEXT.pageTitle}>
      <Container fluid className="gr-page" dir="rtl">
        <div className="gr-page-head">
          <div>
            <h2 className="gr-page-title">{TEXT.pageTitle}</h2>
            <p className="gr-page-subtitle">{TEXT.pageSubtitle}</p>
          </div>
          <Button variant="link" className="gr-refresh" onClick={refresh} disabled={isFetching}>
            <i className="bi bi-arrow-clockwise" aria-hidden="true"></i> {TEXT.refresh}
          </Button>
        </div>

        {/* The reviews list's own state — the complaints and «صوت الضيف» tabs show their own. */}
        {!['complaints', 'voice'].includes(activeTab) && isLoading && (
          <div className="gr-state"><Spinner animation="border" size="sm" /> {TEXT.loading}</div>
        )}
        {!['complaints', 'voice'].includes(activeTab) && isError && (
          <div className="gr-state gr-error" role="alert">
            {TEXT.loadError}{' '}
            <Button variant="link" className="gr-link" onClick={() => refetch()}>{TEXT.refresh}</Button>
          </div>
        )}
        {taskLoadError && <div className="gr-error" role="alert">{taskLoadError}</div>}

        <Tabs
          activeKey={activeTab}
          // A click on the tab already open does nothing — it must not close the pinned complaint.
          onSelect={(key) => key !== activeTab && setSearchParams(key === 'guests' ? {} : { list: key })}
          className="gr-tabs"
        >
          <Tab eventKey="guests" title={tabTitle(TEXT.tabGuests, guestsWaiting)}>
            {ready && (
              <div className="gr-counts" aria-live="polite">
                <span className="gr-count"><strong>{formatCount(counts.needsApproval)}</strong>{TEXT.countNeedsApproval}</span>
                <span className="gr-count"><strong>{formatCount(counts.needsDecision)}</strong>{TEXT.countNeedsDecision}</span>
                <span className="gr-count"><strong>{formatCount(counts.late)}</strong>{TEXT.countLate}</span>
                {counts.unverified > 0 && (
                  <span className="gr-count"><strong>{formatCount(counts.unverified)}</strong>{TEXT.countUnverified}</span>
                )}
              </div>
            )}

            {ready && !switches.reviewSend && <div className="gr-notice">{TEXT.reviewSendOff}</div>}
            {ready && !switches.thankYouSend && <div className="gr-notice">{TEXT.thankYouSendOff}</div>}

            {ready && items.length === 0 && late.length === 0 && (
              <div className="gr-empty">
                <i className="bi bi-star" aria-hidden="true"></i>
                <p>{TEXT.emptyTitle}</p>
                <p className="gr-muted">{TEXT.emptyHint}</p>
              </div>
            )}

            {late.length > 0 && (
              <section className="gr-section">
                <h3 className="gr-section-title">{TEXT.lateTitle} ({formatCount(late.length)})</h3>
                <p className="gr-muted">{TEXT.lateHint}</p>
                {late.map(row => (
                  <LateCleanRow key={row.taskId} row={row} onOpenTask={openTask} loadingTaskId={loadingTaskId} />
                ))}
              </section>
            )}

            {items.length > 0 && (
              <section className="gr-section">
                <h3 className="gr-section-title">{TEXT.listTitle} ({formatCount(items.length)})</h3>
                {items.map(item => (
                  <GuestReviewCard
                    key={item.id}
                    item={item}
                    switches={switches}
                    onOpenTask={openTask}
                    loadingTaskId={loadingTaskId}
                  />
                ))}
              </section>
            )}
          </Tab>

          <Tab eventKey="replies" title={tabTitle(TEXT.tabReplies, repliesWaiting)}>
            {ready && (
              <div className="gr-counts" aria-live="polite">
                <span className="gr-count"><strong>{formatCount(counts.replies.needsApproval)}</strong>{TEXT.countNeedsApproval}</span>
                <span className="gr-count"><strong>{formatCount(counts.replies.needsDecision)}</strong>{TEXT.countNeedsDecision}</span>
                {counts.replies.notPublished > 0 && (
                  <span className="gr-count"><strong>{formatCount(counts.replies.notPublished)}</strong>{TEXT.countNotPublished}</span>
                )}
              </div>
            )}

            {/* What is switched off — one quiet line, so orange / red on the cards only ever means "look at this".
                Booking.com replies stay off until L43 is lifted — its reviews are answered in the extranet. */}
            {ready && (() => {
              const off = [
                !switches.replySend && TEXT.offReplySend,
                !switches.replyDraft && TEXT.offReplyDraft,
                switches.replyPlatforms && !switches.replyPlatforms.booking && TEXT.offBookingReplies,
                !switches.followUpSend && TEXT.offFollowUpSend
              ].filter(Boolean);
              return off.length > 0 && (
                <div className="gr-notice gr-notice--quiet">
                  <i className="bi bi-pause-circle" aria-hidden="true"></i> {TEXT.switchesOff} {off.join('، ')}
                </div>
              );
            })()}

            {ready && replies.length === 0 && (
              <div className="gr-empty">
                <i className="bi bi-chat-square-quote" aria-hidden="true"></i>
                <p>{TEXT.repliesEmptyTitle}</p>
                <p className="gr-muted">{TEXT.repliesEmptyHint}</p>
              </div>
            )}

            {replies.map(reply => (
              <ReplyCard key={reply.id} reply={reply} switches={switches} />
            ))}
          </Tab>

          <Tab eventKey="complaints" title={complaintsTitle}>
            {activeTab === 'complaints' && <ComplaintsTab />}
          </Tab>

          {switches.analysis && (
            <Tab eventKey="voice" title={VOICE_TEXT.tabTitle}>
              {activeTab === 'voice' && <GuestVoiceTab />}
            </Tab>
          )}
        </Tabs>
      </Container>

      <EditTaskModal
        show={!!editingTask}
        task={editingTask}
        onHide={() => setEditingTask(null)}
        onTaskUpdated={handleTaskUpdated}
      />
    </DesktopLayout>
  );
};

export default GuestReviewsPage;
